// client/components/evaluate/ScopeDecisionDialog.tsx
import React, { useState } from 'react';
import type { CreateDecisionPayload } from '../../hooks/useFairDecisions';

export interface ScopeDecisionDialogProps {
  ruleId: string;
  scopeType: 'catalog' | 'schema';
  scopeNaturalKey: string;
  machineVerdictAtDecisionTime: 'PASS' | 'FAIL' | 'NOT_APPLICABLE' | null;
  onSubmit: (payload: CreateDecisionPayload) => Promise<void>;
  onCancel: () => void;
}

export function ScopeDecisionDialog({
  ruleId,
  scopeType,
  scopeNaturalKey,
  machineVerdictAtDecisionTime,
  onSubmit,
  onCancel,
}: ScopeDecisionDialogProps) {
  const [action, setAction] = useState<'accept' | 'reject' | 'edit'>('accept');
  // DATA-INTEGRITY: default the asserted status to the MACHINE verdict, not a
  // hardcoded 'PASS'. Otherwise clicking "Accept" on a machine FAIL submitted
  // PASS and mass-flipped FAILs to PASS at scope level.
  const [assertedStatus, setAssertedStatus] = useState<'PASS' | 'FAIL' | 'NOT_APPLICABLE'>(
    machineVerdictAtDecisionTime ?? 'PASS',
  );
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Accept" means "accept the machine verdict as-is" — so the asserted status
  // is the machine verdict, never the (editable) radio. Only "Edit" lets the
  // user assert a different status.
  const effectiveStatus: 'PASS' | 'FAIL' | 'NOT_APPLICABLE' =
    action === 'accept' ? (machineVerdictAtDecisionTime ?? assertedStatus) : assertedStatus;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        rule_id: ruleId,
        scope_type: scopeType,
        scope_natural_key: scopeNaturalKey,
        action,
        asserted_status: effectiveStatus,
        rationale: rationale.trim() || null,
        machine_verdict_at_decision_time: machineVerdictAtDecisionTime,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0f172a',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 10,
          padding: 24,
          width: 520,
          color: '#e2e8f0',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>
          Scope-level decision · {ruleId}
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '4px 0 16px 0' }}>
          Apply at{' '}
          <strong style={{ color: '#cbd5e1' }}>{scopeType}</strong> scope:{' '}
          <span
            style={{ fontFamily: 'JetBrains Mono, Consolas, monospace', color: '#a5b4fc' }}
          >
            {scopeNaturalKey}
          </span>
        </p>

        <Field label="Action">
          <Radio
            value={action}
            options={[
              { value: 'accept', label: 'Accept' },
              { value: 'reject', label: 'Reject' },
              { value: 'edit', label: 'Edit' },
            ]}
            onChange={(v) => setAction(v as 'accept' | 'reject' | 'edit')}
            name="bulk-action"
          />
        </Field>

        <Field label={action === 'accept' ? 'Asserted status (machine verdict)' : 'Asserted status'}>
          <Radio
            value={effectiveStatus}
            options={[
              { value: 'PASS', label: 'PASS' },
              { value: 'FAIL', label: 'FAIL' },
              { value: 'NOT_APPLICABLE', label: 'N/A' },
            ]}
            // On "Accept" the status is the machine verdict and not editable;
            // changes are only honored for "Edit"/"Reject".
            onChange={(v) => {
              if (action !== 'accept') setAssertedStatus(v as 'PASS' | 'FAIL' | 'NOT_APPLICABLE');
            }}
            name="bulk-status"
          />
        </Field>

        <Field label="Rationale">
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder={`Why this decision applies to all datasets in ${scopeNaturalKey}.`}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: 8,
              borderRadius: 6,
              background: 'rgba(15,23,42,0.6)',
              color: '#e2e8f0',
              border: '1px solid rgba(255,255,255,0.1)',
              fontFamily: 'inherit',
              fontSize: '0.85rem',
              resize: 'vertical',
            }}
          />
        </Field>

        {error && (
          <div style={{ marginTop: 8, color: '#f87171', fontSize: '0.8rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              background: 'transparent',
              color: '#94a3b8',
              border: '1px solid rgba(255,255,255,0.15)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              background: 'rgba(34,197,94,0.18)',
              color: '#4ade80',
              border: '1px solid rgba(34,197,94,0.4)',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.5 : 1,
            }}
          >
            {submitting ? 'Saving…' : 'Submit decision'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#94a3b8',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

interface RadioProps {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
  name: string;
}

function Radio({ value, options, onChange, name }: RadioProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {options.map((opt) => (
        <label key={opt.value} style={{ cursor: 'pointer', color: '#e2e8f0', fontSize: '0.85rem' }}>
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            style={{ marginRight: 6 }}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}
